import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { apiRequest } from '../utils/apiHelper';
import { useNavigate } from 'react-router-dom';

const AuthContext = createContext();

export function AuthProvider({ children }) {
  const [user, setUser] = useState(() => {
    const storedUser = localStorage.getItem('auth_user');
    if (storedUser) {
      try {
        return JSON.parse(storedUser);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        return null;
      }
    }
    return null;
  });
  const [token, setToken] = useState(() => localStorage.getItem('auth_token'));
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Token ve User state'ini localStorage değişiklikleriyle senkronize et
    const handleStorageChange = (e) => {
      if (e.key === 'auth_token') {
        const storedToken = localStorage.getItem('auth_token');
        const storedUser = localStorage.getItem('auth_user');

        if (!storedToken) {
          // Başka sekmede logout olunmuş
          setToken(null);
          setUser(null);
          navigate('/login');
        } else if (storedToken !== token && storedUser) {
          // Başka sekmede login olunmuş veya token yenilenmiş
          setToken(storedToken);
          try {
            setUser(JSON.parse(storedUser));
          } catch (err) {
            console.error('Storage sync user parse error:', err);
          }
        }
      }
    };

    // apiHelper'dan gelen zorunlu logout sinyalini dinle
    const handleAuthLogout = () => {
      setToken(null);
      setUser(null);
      navigate('/login');
    };

    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('auth-logout', handleAuthLogout);

    // Initial check
    const storedUser = localStorage.getItem('auth_user');
    const refreshToken = localStorage.getItem('auth_refresh_token');

    if (token && storedUser) {
      try {
        setUser(JSON.parse(storedUser));
        setLoading(false);
      } catch (error) {
        console.error('Failed to parse stored user:', error);
        logout();
      }
    } else if (!token && refreshToken && storedUser) {
      console.log('Token bulunamadı, refresh token ile yenileme deneniyor...');

      const attemptRefresh = async (retryCount = 0) => {
        let shouldStopLoading = true;
        try {
          const response = await apiRequest('/auth/refresh-token', {
            method: 'POST',
            data: { refreshToken },
            skipAuth: true
          });

          if (response.success !== false && response.accessToken) {
            console.log('Token başarıyla yenilendi');
            localStorage.setItem('auth_token', response.accessToken);
            if (response.refreshToken) {
              localStorage.setItem('auth_refresh_token', response.refreshToken);
            }
            setToken(response.accessToken);

            try {
              setUser(JSON.parse(storedUser));
            } catch (error) {
              console.error('Failed to parse stored user:', error);
              logout();
            }
          } else {
            console.error('Token yenileme başarısız:', response.message);
            // Sadece 401/403 gibi kesin auth hatalarında logout yap
            // Timeout/Network hatalarında logout yapma (backend'den status gelmiyorsa response.success false olabilir)
            logout();
          }
        } catch (error) {
          console.error(`Token yenileme hatası (Deneme ${retryCount + 1}):`, error);

          // Timeout veya Network hatası ise ve deneme sayısı < 3 ise tekrar dene
          // apiHelper'dan gelen hata yapısına göre kontrol edilmeli
          const isNetworkOrTimeout = !error.response || error.code === 'ECONNABORTED' || error.message.includes('timeout') || error.status >= 500;

          if (isNetworkOrTimeout && retryCount < 2) {
            console.log('Hata geçici olabilir, 1 saniye sonra tekrar deneniyor...');
            setTimeout(() => attemptRefresh(retryCount + 1), 1000);
            shouldStopLoading = false;
            return; // Loop'tan çık, yeni deneme setTimeout içinde
          }

          // Max deneme sayısına ulaşıldı veya kesin hata (4xx)
          logout();
        } finally {
          // Sadece son deneme ise loading'i kapat
          // (Not: setTimeout ile çağrılan recursivelerde bu finally çalışır ama loading state güncellemesi her seferinde sorun olmaz)
          if (shouldStopLoading) {
            setLoading(false);
          }
        }
      };

      attemptRefresh();
    } else {
      setLoading(false);
    }

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('auth-logout', handleAuthLogout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRedirect = useCallback((userRole) => {
    if (userRole === 'SHIPPING_STAFF') {
      navigate('/admin/orders');
    } else if (userRole === 'SUPER_ADMIN' || userRole === 'COMPANY_ADMIN') {
      navigate('/admin');
    } else {
      navigate('/products');
    }
  }, [navigate]);

  const logout = useCallback(async () => {
    try {
      const refreshToken = localStorage.getItem('auth_refresh_token');
      if (refreshToken) {
        // Token'ı revoke etmeyi dene, ama hata alsa bile logout işlemine devam et
        await apiRequest('/auth/revoke-token', {
          method: 'POST',
          data: { refreshToken },
          skipAuth: true // Revoke işlemi için auth header göndermeye gerek yok (veya gerekebilir, duruma göre)
        }).catch(err => console.warn('Token revocation failed:', err));
      }
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      localStorage.removeItem('auth_refresh_token');
      localStorage.removeItem('auth_user');
      setToken(null);
      setUser(null);
      navigate('/login');
    }
  }, [navigate]);

  const login = async (email, password) => {
    try {
      const response = await apiRequest('/auth/login', {
        method: 'POST',
        data: { email, password },
        skipAuth: true
      });

      console.log('Login response:', response);

      if (response.success) {
        // Yeni şemaya göre accessToken ve refreshToken
        const accessToken = response.accessToken || response.token;
        const refreshToken = response.refreshToken;

        if (accessToken) {
          localStorage.setItem('auth_token', accessToken);
          setToken(accessToken);
        }

        if (refreshToken) {
          localStorage.setItem('auth_refresh_token', refreshToken);
        }

        if (response.user) {
          localStorage.setItem('auth_user', JSON.stringify(response.user));
          setUser(response.user);
        }

        return {
          success: true,
          user: response.user
        };
      }

      // Başarısız login
      return {
        success: false,
        message: response.message || 'Giriş başarısız'
      };
    } catch (error) {
      console.error('Auth login error:', error);
      if (error.apiError) {
        return {
          success: false,
          message: 'Sunucuya ulaşılamıyor. Lütfen internet bağlantınızı kontrol edin veya tekrar deneyin.'
        };
      }
      return {
        success: false,
        message: error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.'
      };
    }
  };

  const register = async (formData) => {
    try {
      const response = await apiRequest('/auth/signup', {
        method: 'POST',
        data: formData,
        skipAuth: true
      });

      if (response.success) {
        // Mesajın gösterilmesi için kısa bir gecikme ekle
        setTimeout(() => {
          navigate('/login');
        }, 1500);

        return {
          success: true,
          message: response.message || 'Kayıt başarılı!'
        };
      } else {
        return {
          success: false,
          message: response.message || 'Kayıt başarısız'
        };
      }
    } catch (error) {
      console.error('Auth register error:', error);
      return {
        success: false,
        message: error.message || 'Kayıt olurken bir hata oluştu'
      };
    }
  };

  const resendVerification = async (email) => {
    try {
      const response = await apiRequest('/auth/resend-verification', {
        method: 'POST',
        data: { email },
        skipAuth: true
      });
      return {
        success: response.success !== false,
        message: response.message || 'Doğrulama maili tekrar gönderildi'
      };
    } catch (error) {
      console.error('Resend verification error:', error);
      return {
        success: false,
        message: error.message || 'Mail gönderilirken bir hata oluştu'
      };
    }
  };

  const autoRegisterAndLogin = async (customerInfo) => {
    try {
      const response = await apiRequest('/auth/auto-register', {
        method: 'POST',
        data: customerInfo,
        skipAuth: true
      });

      if (response.success) {
        const accessToken = response.accessToken || response.token;
        const refreshToken = response.refreshToken;

        if (accessToken) {
          localStorage.setItem('auth_token', accessToken);
          setToken(accessToken);
        }

        if (refreshToken) {
          localStorage.setItem('auth_refresh_token', refreshToken);
        }

        localStorage.setItem('auth_user', JSON.stringify(response.user));
        setUser(response.user);
        handleRedirect(response.user.role);
        return {
          success: true,
          message: 'Hesabınız başarıyla oluşturuldu!'
        };
      } else {
        return {
          success: false,
          message: response.message || 'Hesap oluşturulamadı'
        };
      }
    } catch (error) {
      console.error('Auto register error:', error);
      if (error.apiError) {
        return {
          success: false,
          message: 'Sunucuya ulaşılamıyor. Lütfen internet bağlantınızı kontrol edin veya tekrar deneyin.'
        };
      }
      return {
        success: false,
        message: error.message || 'Bir hata oluştu. Lütfen tekrar deneyin.'
      };
    }
  };

  const updateUserInfo = useCallback((userInfo) => {
    try {
      const updatedUser = {
        ...user,
        ...userInfo
      };
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to update user info:', error);
    }
  }, [user]);

  const updateDeliveryInfo = useCallback((deliveryInfo) => {
    try {
      const updatedUser = {
        ...user,
        deliveryInfo: {
          ...user?.deliveryInfo,
          ...deliveryInfo
        }
      };
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to update delivery info:', error);
    }
  }, [user]);

  const updateBillingInfo = useCallback((billingInfo) => {
    try {
      const updatedUser = {
        ...user,
        billingInfo: {
          ...user?.billingInfo,
          ...billingInfo
        }
      };
      localStorage.setItem('auth_user', JSON.stringify(updatedUser));
      setUser(updatedUser);
    } catch (error) {
      console.error('Failed to update billing info:', error);
    }
  }, [user]);

  const value = {
    user,
    setUser,
    token,
    setToken,
    loading,
    login,
    logout,
    register,
    resendVerification,
    autoRegisterAndLogin,
    updateUserInfo,
    updateDeliveryInfo,
    updateBillingInfo,
    isAuthenticated: !!token && !!user,
    role: user?.role || null
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
} 